// ** Redux Imports
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"

// ** API
import API from "api/chines_orders"
import DocumentsAPI from "api/documents"

// ---------------------------------------------
// LIST CHINES ORDERS
// ---------------------------------------------
export const getData = createAsyncThunk(
  "chinesOrders/getData",
  async (params, { signal }) => {
    const response = await API.listChinesOrders(params)
    if (signal.aborted) return

    return {
      category: params.category,
      params,
      data: response.data || [],
      totalRows: response.totalRows || 0
    }
  }
)

// ---------------------------------------------
// GET ONE (VIEW PAGE)
// ---------------------------------------------
export const getChinesOrder = createAsyncThunk(
  "chinesOrders/getOne",
  async (name, { dispatch, getState }) => {
    const loaded = getState().chinesOrders.data?.filter(e => e.name === name)[0]
    if (loaded) dispatch(chinesOrdersSlice.actions.setSelected(loaded))
    return await API.getChinesOrder(name)
  }
)

// ---------------------------------------------
// CREATE CHINES ORDER
// ---------------------------------------------
export const createChinesOrder = createAsyncThunk(
  "chinesOrders/create",
  async (order, { dispatch, getState }) => {
    dispatch(chinesOrdersSlice.actions.setUpdating(true))

    const created = await API.createChinesOrder(order)

    dispatch(chinesOrdersSlice.actions.setUpdating(false))

    // Reload list with same filters
    // await dispatch(getData(getState().chinesOrders.params))

    const params = getState().chinesOrders.params 
    if (params) {
      await dispatch(getData(params))
    }

    return created
  }
)

// ---------------------------------------------
// UPDATE ORDER
// ---------------------------------------------
export const updateChinesOrder = createAsyncThunk(
  "chinesOrders/update",
  async (order, { dispatch, getState }) => {
    dispatch(chinesOrdersSlice.actions.setUpdating(true))

    const updated = await API.updateChinesOrder(order)

    dispatch(chinesOrdersSlice.actions.setUpdating(false))
    // Refresh selected
    dispatch(chinesOrdersSlice.actions.setSelected(updated))

    // Refresh list with same filters
    // await dispatch(getData(getState().chinesOrders.params))

    const params = getState().chinesOrders.params
    if (params?.category) {
      await dispatch(getData(params))
    }

    return updated
  }
)

// ---------------------------------------------
// ADD PAYMENT
// ---------------------------------------------
export const addChinesOrderPayment = createAsyncThunk(
  'chinesOrders/addPayment',
  async ({ payment }, { dispatch, rejectWithValue }) => {

    // TEMP ID for UI
    const optimisticPayment = {
      ...payment,
      name: `temp-${Date.now()}`
    }

    // 👇 UI updates immediately
    dispatch(chinesOrdersSlice.actions.addPaymentOptimistic(optimisticPayment))

    try {
      const created = await API.createPayment(payment)

      // Replace temp with real one
      await dispatch(getChinesOrderPayments(payment.chinesOrderName))
      await dispatch(getChinesOrder(payment.chinesOrderName))

      return created
    } catch (err) {
      // ❌ rollback
      dispatch(
        chinesOrdersSlice.actions.deletePaymentOptimistic(
          optimisticPayment.name
        )
      )
      return rejectWithValue(err)
    }
  }
)

// ---------------------------------------------
// GET PAYMENTS
// ---------------------------------------------
export const getChinesOrderPayments = createAsyncThunk(
  'chinesOrders/getPayments',
  async (chinesOrderName) => {
    return await API.listPayments(chinesOrderName)
  }
)

// ---------------------------------------------
// UPDATE PAYMENT
// ---------------------------------------------
export const updateChinesOrderPayment = createAsyncThunk(
  'chinesOrders/updatePayment',
  async ({ payment }, { dispatch, getState, rejectWithValue }) => {

    const prev = getState().chinesOrders.payments
      .find(p => p.name === payment.name)

    // optimistic update
    dispatch(chinesOrdersSlice.actions.updatePaymentOptimistic(payment))

    try {
      await API.updatePayment({ payment })

      await dispatch(getChinesOrderPayments(payment.chinesOrderName))
      await dispatch(getChinesOrder(payment.chinesOrderName))
      
      return true
    } catch (err) {
      // rollback
      dispatch(
        chinesOrdersSlice.actions.updatePaymentOptimistic(prev)
      )
      return rejectWithValue(err)
    }
  }
)

// ---------------------------------------------
// DELETE PAYMENT
// ---------------------------------------------
export const deleteChinesOrderPayment = createAsyncThunk(
  'chinesOrders/deletePayment',
  async ({ paymentName, chinesOrderName }, { dispatch, getState, rejectWithValue }) => {

    const prev = getState().chinesOrders.payments.find(p => p.name === paymentName)

    // instant remove
    dispatch(chinesOrdersSlice.actions.deletePaymentOptimistic(paymentName))

    try {
      await API.deletePayment(paymentName)

      await dispatch(getChinesOrderPayments(chinesOrderName))
      await dispatch(getChinesOrder(chinesOrderName))

      return true
    } catch (err) {
      // rollback
      dispatch(
        chinesOrdersSlice.actions.addPaymentOptimistic(prev)
      )
      return rejectWithValue(err)
    }
  }
)

export const getChinesOrderDocuments = createAsyncThunk(
  "chinesOrders/getDocuments",
  async (chinesOrderName) => {
    const response = await DocumentsAPI.listDocuments({
      location: chinesOrderName
    })
    return { [chinesOrderName]: response }
  }
)

export const getChinesOrderDocument = createAsyncThunk(
  "chinesOrders/getDocument",
  async (name) => {
    return await DocumentsAPI.getDocument(name)
  }
)

export const addChinesOrderDocument = createAsyncThunk(
  "chinesOrders/addDocument",
  async (document) => {
    const updated = await DocumentsAPI.createDocument(document)
    return { ...document, ...updated }
  }
)

export const deleteChinesOrderDocument = createAsyncThunk(
  "chinesOrders/deleteDocument",
  async ({ name, chinesOrderName }, { dispatch }) => {
    await DocumentsAPI.deleteDocument(name)
    await dispatch(getChinesOrderDocuments(chinesOrderName))
    return name
  }
)


// ---------------------------------------------
// SLICE
// ---------------------------------------------
export const chinesOrdersSlice = createSlice({
   name: "chinesOrders",
    initialState: {
    params: null,
      lists: {
        chines_car: {
          data: [],
          totalRows: 0,
          loading: false
        }
      },

        updating: false,
        orderLoading: false,
        error: null,
        selectedOrder: null,
        payments: [],
        documents: {},
    },
  reducers: {
    setUpdating: (state, { payload }) => {
      state.updating = payload
    },
    setSelected: (state, { payload }) => {
      state.selectedOrder = payload
    },
    setPayment: (state, { payload }) => {
      state.payments = payload
    },
    addPaymentOptimistic: (state, { payload }) => {
      if (!payload) return
      state.payments.unshift(payload)
    },
    updatePaymentOptimistic: (state, { payload }) => {
      if (!payload) return
      state.payments = state.payments.map(p =>
      p.name === payload.name ? payload : p
     )
    },
    deletePaymentOptimistic: (state, { payload }) => {
      state.payments = state.payments.filter(p => p.name !== payload)
    }
  },
  extraReducers: builder => {
    builder

      // LIST PENDING
      .addCase(getData.pending, (state, action) => {
        const category = action.meta.arg.category

        // 🛡️ SAFETY GUARD
        if (!state.lists[category]) return

        state.lists[category].loading = true
        state.lists[category].requestId = action.meta.requestId
        state.error = null
      })

      // LIST FULFILLED
      .addCase(getData.fulfilled, (state, { payload, meta }) => {
        if (!payload) return
        const { category, data, totalRows, params } = payload
        if (state.lists[category]?.requestId !== meta.requestId) return

        // 🛡️ SAFETY GUARD
        if (!state.lists[category]) return

        state.params = params

        state.lists[category] = {
          data,
          totalRows,
          loading: false
        }
      })

      // LIST REJECTED
      .addCase(getData.rejected, (state, action) => {
        const category = action.meta.arg.category

        // 🛡️ SAFETY GUARD
        if (!state.lists[category]) return

        if (state.lists[category].requestId !== action.meta.requestId) return
        state.lists[category].loading = false
        state.error = action.error.message
      })

      // VIEW
      .addCase(getChinesOrder.pending, (state, action) => {
        state.orderRequestId = action.meta.requestId
        state.selectedOrder = null
        state.error = null
        state.orderLoading = true
      })
      .addCase(getChinesOrder.fulfilled, (state, { payload, meta }) => {
        if (state.orderRequestId !== meta.requestId) return
        state.orderLoading = false
        state.selectedOrder = payload
      })
      .addCase(getChinesOrder.rejected, (state, action) => {
        if (state.orderRequestId !== action.meta.requestId) return
        state.orderLoading = false
        state.error = action.error.message
      })
      .addCase(createChinesOrder.rejected, (state, action) => {
        state.updating = false
        state.error = action.error.message
      })
      .addCase(updateChinesOrder.rejected, (state, action) => {
        state.updating = false
        state.error = action.error.message
      })
      .addCase(getChinesOrderPayments.pending, (state, action) => {
        state.paymentRequestId = action.meta.requestId
        state.payments = []
      })
      .addCase(getChinesOrderPayments.fulfilled, (state, { payload, meta }) => {
        if (state.paymentRequestId !== meta.requestId) return
        state.payments = payload
      })
      .addCase(getChinesOrderDocuments.fulfilled, (state,{payload}) => {
        state.documents = {
          ...state.documents,
          ...payload
        }
      }
    )
  }
})

export default chinesOrdersSlice.reducer
